import { createI18n } from 'vue-i18n'
import zhCN from '@/locales/zh-CN.json'
import en from '@/locales/en.json'
import ja from '@/locales/ja.json'
import ko from '@/locales/ko.json'
import fr from '@/locales/fr.json'
import de from '@/locales/de.json'
import ru from '@/locales/ru.json'
import ar from '@/locales/ar.json'
import { getCookie } from '@/utils/cookie'

export const localeCodes = ['zh-CN', 'en', 'ja', 'ko', 'fr', 'de', 'ru', 'ar'] as const
export type LocaleCode = (typeof localeCodes)[number]
export const localeRoutePattern = 'zh|zh-cn|en|ja|ko|fr|de|ru|ar'

export const localeToRouteSegment = (locale: LocaleCode): string =>
  locale === 'zh-CN' ? 'zh' : locale

export const routeSegmentToLocale = (value?: string | null): LocaleCode | undefined => {
  if (!value) return undefined
  const normalized = value.toLowerCase()
  if (normalized === 'zh' || normalized === 'zh-cn') return 'zh-CN'
  return localeCodes.find((code) => code.toLowerCase() === normalized)
}

export const localeOptions: Array<{
  code: LocaleCode
  label: string
  short: string
  dir: 'ltr' | 'rtl'
}> = [
  { code: 'zh-CN', label: '中文', short: 'ZH', dir: 'ltr' },
  { code: 'en', label: 'English', short: 'EN', dir: 'ltr' },
  { code: 'ko', label: '한국어', short: 'KO', dir: 'ltr' },
  { code: 'fr', label: 'Français', short: 'FR', dir: 'ltr' },
  { code: 'de', label: 'Deutsch', short: 'DE', dir: 'ltr' },
  { code: 'ru', label: 'Русский', short: 'RU', dir: 'ltr' },
  { code: 'ar', label: 'العربية', short: 'AR', dir: 'rtl' },
  { code: 'ja', label: '日本語', short: 'JA', dir: 'ltr' },
]

const browserLocale = (): LocaleCode => {
  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of browserLanguages) {
    const normalized = language.toLowerCase()
    if (normalized.startsWith('zh')) return 'zh-CN'
    const match = localeCodes.find((code) => normalized.startsWith(code.toLowerCase()))
    if (match) return match
  }
  return 'en'
}

const defaultLocale = (): LocaleCode => {
  const configured = import.meta.env.VITE_APP_DEFAULT_LOCALE?.trim()
  if (!configured || configured.toLowerCase() === 'browser') return browserLocale()
  return routeSegmentToLocale(configured) || 'en'
}

export const initialLocale = routeSegmentToLocale(getCookie('locale')) || defaultLocale()

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'en',
  missingWarn: false,
  fallbackWarn: false,
  messages: { 'zh-CN': zhCN, en, ja, ko, fr, de, ru, ar },
})

export default i18n
